import { IPost } from '_interfaces/post.interface';
import { IPostType, ISystemPostMetadata } from '_interfaces/systemPost.types';
import { getMimeTypeFromFileName } from 'utils/helpers/fileCabinetHelpers';

/**
 * Type guards for system post metadata
 */
export function isSelectionItemPost(
  metadata: ISystemPostMetadata | undefined
): metadata is Extract<ISystemPostMetadata, { type: 'SELECTION_ITEM_POST' }> {
  return metadata?.type === 'SELECTION_ITEM_POST';
}

export function isFileCommentPost(
  metadata: ISystemPostMetadata | undefined
): metadata is Extract<ISystemPostMetadata, { type: 'FILE_COMMENT' }> {
  return metadata?.type === 'FILE_COMMENT';
}

export function isScheduleItemPost(
  metadata: ISystemPostMetadata | undefined
): metadata is Extract<ISystemPostMetadata, { type: 'SCHEDULE_ITEM_POST' }> {
  return metadata?.type === 'SCHEDULE_ITEM_POST';
}

/**
 * Navigation helpers for system posts
 */
export interface NavigationHandlers {
  navigateToSelection?: (selectionId: string, selectionItemId: string) => void;
  navigateToDocument?: (fileId: string, fileName: string, fileSize: string, mimeType: string) => void;
  navigateToSchedule?: (scheduleId: string, scheduleItemId: string) => void;
}

/**
 * Get the appropriate navigation handler for a system post
 */
export function getSystemPostNavigation(
  post: IPost,
  handlers: NavigationHandlers
): (() => void) | undefined {
  if (!post.isSystemPost() || !post.systemMetadata) {
    return undefined;
  }

  const metadata = post.systemMetadata;

  if (isSelectionItemPost(metadata) && handlers.navigateToSelection) {
    const { selectionId, selectionItemId } = metadata.linkData;
    return () => handlers.navigateToSelection?.(selectionId, selectionItemId);
  }

  if (isFileCommentPost(metadata) && handlers.navigateToDocument) {
    const { fileId, documentId, commentId, fileName } = metadata.linkData;
    const mimeType = getMimeTypeFromFileName(fileName);
    return () => handlers.navigateToDocument?.(fileId, fileName, '', mimeType);
  }

  if (isScheduleItemPost(metadata) && handlers.navigateToSchedule) {
    const { scheduleId, scheduleItemId } = metadata.linkData;
    return () => handlers.navigateToSchedule?.(scheduleId, scheduleItemId);
  }

  return undefined;
}

/**
 * Get the icon name for a system post type
 */
export function getSystemPostIcon(postType: IPostType): string {
  switch (postType) {
    case IPostType.SELECTION_ITEM_POST:
      return 'check-circle';
    case IPostType.FILE_COMMENT:
      return 'message-square';
    case IPostType.SCHEDULE_ITEM_POST:
      return 'calendar';
    case IPostType.USER_POST:
    default:
      return 'message';
  }
}

/**
 * Get a human-readable label for a system post type
 */
export function getSystemPostLabel(postType: IPostType): string {
  switch (postType) {
    case IPostType.SELECTION_ITEM_POST:
      return 'Selection Updated';
    case IPostType.FILE_COMMENT:
      return 'New Comment';
    case IPostType.SCHEDULE_ITEM_POST:
      return 'Schedule Updated';
    case IPostType.USER_POST:
    default:
      return 'Post';
  }
}
